import { SortingState } from '@tanstack/react-table';
import { useLayoutTier } from '../../../hooks/useLayoutTier';
import { ErrorDisplay } from '../../ErrorDisplay';
import { DataTable } from '../DataTable';
import { MobileList } from '../MobileList';
import { MobileSelectionBar } from '../MobileList/MobileSelectionBar';
import { ResponsiveDataViewProps } from './types';

/**
 * ResponsiveDataView - Automatically switches between desktop table and mobile list
 * based on screen size
 */
export function ResponsiveDataView<TData>({
  data,
  isLoading = false,
  onRefresh,
  error,
  getRowId,
  columns,
  getSubRows,
  expanded,
  onExpandedChange,
  renderListItem,
  enableSelection = false,
  selectedIds = [],
  onSelectionChange,
  selectionMode,
  onSelectionModeChange,
  onRowClick,
  pageIndex,
  pageSize,
  totalPages,
  totalCount,
  onPageChange,
  onPageSizeChange,
  columnFilters,
  toolbarActions,
  activeFilters,
  enableManageColumns,
  multiSelectActions,
  mobileSelectionActions,
  emptyState,
  emptyStateMessage,
  sorting,
  onSortingChange,
  columnPinning,
  onColumnPinningChange,
  columnVisibility,
  onColumnVisibilityChange,
  defaultColumnVisibility,
  columnOrder,
  onColumnOrderChange,
  columnSizing,
  onColumnSizingChange,
  resetColumnSizing,
  compact,
  activeId,
  ariaLabel,
  title,
  icon,
}: ResponsiveDataViewProps<TData>) {
  const { tier } = useLayoutTier();

  const showMobile = tier !== 'desktop';
  // A page that passes no compact config keeps the paginated list on a phone
  // and on a tablet.
  const compactConfig = showMobile ? compact : undefined;
  const layout = compactConfig?.layout ?? 'list';
  const paginated = compactConfig?.showPagination ?? true;

  // The table sorts through the same state the desktop table uses, so the two
  // can never disagree. The query reads the first entry only.
  const sort =
    onSortingChange && layout === 'table'
      ? {
          columnId: sorting?.[0]?.id,
          descending: sorting?.[0]?.desc ?? false,
          onSortChange: (
            columnId: string,
            direction: 'asc' | 'desc' | null
          ) => {
            const next: SortingState =
              direction === null
                ? []
                : [{ id: columnId, desc: direction === 'desc' }];

            onSortingChange(next);
          },
        }
      : undefined;

  // Default getRowId if not provided
  const getItemId =
    getRowId ||
    ((item: TData) => {
      if (item && typeof item === 'object' && 'id' in item) {
        return String(item.id);
      }
      throw new Error(
        'ResponsiveDataView: getRowId is required when data items do not have an "id" property'
      );
    });

  if (showMobile) {
    const hasSelection = enableSelection && selectedIds.length > 0;

    // Mobile List View
    return (
      <div
        className='relative flex h-full max-w-full flex-1 flex-col
          overflow-hidden'
      >
        {/* Mobile List with bottom padding when selection bar is visible */}
        {compactConfig?.toolbar}

        <div
          data-list-scroll
          className='max-w-full flex-1 overflow-auto'
          // Room for the floating selection bar: its height plus its 32px offset.
          style={{ paddingBottom: hasSelection ? '104px' : undefined }}
        >
          <MobileList
            data={data}
            isLoading={isLoading}
            onRefresh={onRefresh}
            getRowId={getItemId}
            layout={layout}
            gridClassName={compactConfig?.gridClassName}
            columns={compactConfig?.tableColumns}
            sort={sort}
            getRowLabel={compactConfig?.getRowLabel}
            activeId={activeId}
            renderItem={(item, config) =>
              layout === 'grid' && compactConfig?.renderGridItem
                ? compactConfig.renderGridItem(item, config)
                : renderListItem(item, config)
            }
            enableSelection={enableSelection}
            selectedIds={selectedIds}
            onSelectionChange={onSelectionChange}
            selectionMode={selectionMode}
            onSelectionModeChange={onSelectionModeChange}
            onItemClick={onRowClick}
            title={title}
            icon={icon}
            headerActions={toolbarActions}
            currentPage={paginated ? (pageIndex ?? 0) + 1 : undefined}
            totalPages={paginated ? totalPages : undefined}
            totalCount={totalCount}
            rowsPerPage={pageSize}
            onPageChange={
              paginated ? (page) => onPageChange?.(page - 1) : undefined
            }
            emptyState={
              error ? (
                <ErrorDisplay error={error} />
              ) : emptyState ? (
                emptyState
              ) : (
                <div className='text-center text-gray-500'>
                  <p className='mb-1 text-lg font-medium'>No items found</p>
                  {emptyStateMessage && (
                    <p className='text-sm'>{emptyStateMessage}</p>
                  )}
                </div>
              )
            }
            ariaLabel={ariaLabel}
          />
        </div>

        {/* Selection bar at the bottom of the list */}
        {mobileSelectionActions && (
          <MobileSelectionBar
            selectedCount={selectedIds.length}
            actions={mobileSelectionActions}
          />
        )}
      </div>
    );
  }

  // Desktop Table View
  return (
    <div className='flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-hidden'>
      {/* DataTable */}
      <DataTable
        toolbarActions={
          enableSelection && selectedIds.length > 0 && multiSelectActions
            ? multiSelectActions
            : toolbarActions
        }
        data={data}
        columns={columns}
        isLoading={isLoading}
        error={error}
        getRowId={getItemId}
        pageIndex={pageIndex}
        pageSize={pageSize}
        totalPages={totalPages}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        sorting={sorting}
        onSortingChange={onSortingChange}
        columnPinning={columnPinning}
        onColumnPinningChange={onColumnPinningChange}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={onColumnVisibilityChange}
        defaultColumnVisibility={defaultColumnVisibility}
        columnOrder={columnOrder}
        onColumnOrderChange={onColumnOrderChange}
        getSubRows={getSubRows}
        expanded={expanded}
        onExpandedChange={onExpandedChange}
        columnSizing={columnSizing}
        onColumnSizingChange={onColumnSizingChange}
        resetColumnSizing={resetColumnSizing}
        columnFilters={columnFilters}
        activeFilters={activeFilters}
        enableManageColumns={enableManageColumns}
        onRowClick={onRowClick}
        selectedIds={enableSelection ? selectedIds : undefined}
        emptyStateMessage={emptyStateMessage || 'No data found'}
        emptyStateDescription='Try adjusting your filters or search criteria'
        title={title}
        ariaLabel={ariaLabel}
      />
    </div>
  );
}
